import React from 'react';
import { Editor, EditorState, RichUtils, CompositeDecorator, AtomicBlockUtils } from 'draft-js';
import CustomScrollbar from 'js/common/components/CustomScrollbar';
import RTFControlItem from './RTFControlItem';
import { convertToHTML, convertFromHTML } from 'draft-convert';
import { GLOBAL_CONSTANTS, APP_URL_CONSTANTS } from 'js/common/constants/GlobalConstants';
import { showMessage } from 'js/modules/message/actions/MessageActions';
import { BOARD_MESSAGE_CONSTANT } from 'js/modules/message/constants/MessageConstants';
import Select from 'react-select';
import properties from 'properties';
import AppUtil from 'js/utils/AppUtil';
import AppHelper from 'js/app/AppHelper';
import FileUtil from 'js/utils/FileUtil';

class RTF extends React.Component {
	constructor(props) {
        super(props);
        
        const decorator = new CompositeDecorator([
            {
              strategy: this._findLinkEntities,
              component: this._Link
            },
            {
                strategy: this._findFontEntities,
                component: this._Font
            }
        ]);

		this.state = {
            editorState: this.props.config.defaultValue ? EditorState.createWithContent( 
                this._getRTFStateFromHTML(), decorator ) : EditorState.createEmpty( decorator ),
            showLinkBox: false,
            showImageUploadBox: false,
            uploadImageType: 'internal',
            image: undefined,
            shortcustImagePaste: false,
            fullscreen: false
        };

        this.focus = () => this.refs.editor.focus();
        this.handleKeyCommand = command => this._handleKeyCommand( command );
        this.onTab = e => this._onTab(e);

        this.rtfConst = {
            fontFamily: 'fontFamily',
            fontSize: 'fontSize',
            bgColor: 'backgroundcolor',
            fontColor: 'fontcolor',
            backgroundColor: 'backgroundColor',
            custoDropDown: 'custom_drop_down',
            justify: 'justify',
            justifyLeft: 'justifyLeft',
            justifyRight: 'justifyRight',
            justifyCenter: 'justifyCenter',
            bulletList: GLOBAL_CONSTANTS.UNORDERED_LIST_ITEM,
            numberList: GLOBAL_CONSTANTS.ORDERED_LIST_ITEM,
            image: 'image'
        };
        
        this.refreshDropdown = false;
        this.supportedImageTypes = ["art", "bm", "bmp", "dwg", "dxf", "fif", "flo", "fpx", "g3", "gif", "ico", "ief", "iefs",
            "jfif", "jfif-tbnl", "jpe", "jpeg", "jpg", "jps", "jut", "mcf", "nap", "naplps", "nif", "niff", "pbm", "pct",
            "pcx", "pgm", "pic", "pict", "pm", "png", "pnm", "ppm", "qif", "qti", "qtif", "ras", "rast", "rf", "rgb", "rp",
            "svf", "tif", "tiff", "turbot", "wbmp", "xbm", "xif", "xpm", "x-png", "xwd"];
    };
    
    _findLinkEntities( contentBlock, callback, contentState ) {
        contentBlock.findEntityRanges(
          character => {
            const entityKey = character.getEntity();

            return (
              entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK'
            );
          },
          callback
        );
    };
    _findFontEntities( contentBlock, callback, contentState ) {
        contentBlock.findEntityRanges(
          character => {
            const entityKey = character.getEntity();

            return (
              entityKey !== null && contentState.getEntity(entityKey).getType() === 'FONT'
            );
          },
          callback
        );
    };
	
	render() {
		return (
			<div className={ `rtf_cnt ${ this.state.fullscreen ? 'rtf_full_screen' : '' } ${this.props.required && this.props.error ? " rtf_required" : ""}`}>
                {
                    this.props.config && this.props.config.toolbar && this.props.isEditable &&
                    this._getToolbar()
                }

                <CustomScrollbar onPaste={ this.onRTFPaste.bind(this) } style={ {height: 'calc(100% - ' + ( this.props.isEditable ? this.state.fullscreen ? 30 : 28 : 0 ) + 'px)', width: '100%'} } className="rtf_body">
                    <Editor editorState={ this.state.editorState } onChange={ this.onRtfChange.bind(this) }
                        handleKeyCommand={ this.handleKeyCommand } blockStyleFn={ this._getBlockStyle.bind(this) }
                        customStyleMap={ this._getCustomStyleMap() } textAlignment='right' onTab={ this.onTab }
                        ref='editor' spellCheck={ true } readOnly={ !this.props.isEditable }
                        blockRendererFn={ this.mediaBlockRenderer.bind(this) } ></Editor>
                </CustomScrollbar>
            </div>
		);
    };

    /* istanbul ignore next */
    Image(props) {
        if (!!props.src) {
            return <img src={ props.src } alt='rtfimage'/>;
        }

        return null;
    };

    /* istanbul ignore next */
    Media(props) {
        const entity = props.contentState.getEntity( props.block.getEntityAt(0) );
        const { src } = entity.getData();
        const type = entity.getType();
        
        let media;
        
        if ( type === "image" ) {
            media = <img src={ src } alt='rtfimage'/>;
        }
        
        return media;
    };

    /* istanbul ignore next */
    mediaBlockRenderer (block) {
        if (block.getType() === "atomic") {
            return {
                component: this.Media,
                editable: false
            };
        }

        return null;
    };

    /* istanbul ignore next */
    onRtfChange( newEditorState ) {
        const currentContentState = this.state.editorState.getCurrentContent();
        const newContentState = newEditorState.getCurrentContent();

        const isRtfChanged = currentContentState !== newContentState;

        this.setState( {
            editorState: newEditorState
        }, () => {
            if ( isRtfChanged && this.props.onChange ) {
                this.props.onChange( this._getRTFContent() );
            }
        } );
    };

    /* istanbul ignore next */
    _handleKeyCommand( command ) {
        const newState = RichUtils.handleKeyCommand( this.state.editorState, command );

        if ( newState ) {
            this.onRtfChange( newState );

            return true;
        }

        return false;
    };

    /* istanbul ignore next */
    _onTab( event ) {
        const maxDepth = 4;

        this.onRtfChange( RichUtils.onTab( event, this.state.editorState, maxDepth ) );
    };

    _getToolbar() {
        const toolbarItems = this.props.config.toolbar.split( '|' );
        this.refreshDropdown = !this.refreshDropdown;
        const uploadOptions = [{
            id: 'internal',
            value: 'internal',
            label: 'From Device'
        }, {
            id: 'url',
            value: 'url',
            label: 'URL'
        }];

        return (
            <div className='rtf_toolbar_cnt'>
                {
                    (() => {
                        let toolbarCntKey = 0;

                        return toolbarItems.map( toolbarItem => {
                            const items = toolbarItem.trim().split( ' ' )
                            
                            if  ( items.length > 0 ) {
                                return (
                                    <div key={ 'toolbarCnt_' + toolbarCntKey++ } className='toolbar_box'>
                                        {
                                            items.map( item => {
                                                const currentStyle = this.state.editorState.getCurrentInlineStyle();
                                                const itemCfg = this._getToolbarItem( item.trim().toLocaleLowerCase() );
                                                let isActive = false;

                                                if ( itemCfg && itemCfg.styleType === 'block' ) {
                                                    const selection = this.state.editorState.getSelection();
                                                    const blockType = this.state.editorState.getCurrentContent()
                                                        .getBlockForKey( selection.getStartKey() )
                                                        .getType();
                                                        
                                                    isActive = item.toLocaleUpperCase() === blockType.toLocaleUpperCase();
                                                    
                                                    if ( itemCfg.type === this.rtfConst.custoDropDown ) {
                                                        if ( isActive ) {
                                                            const selectedBlockEl = document.querySelector('div[data-offset-key^="' + selection.getStartKey() + '"]');
    
                                                            if ( selectedBlockEl ) {
                                                                itemCfg.dropDownCfg.selected = selectedBlockEl.className === '' ? this._getBlockStyle( undefined, this.rtfConst.fontFamily ) : selectedBlockEl.className;
                                                            }
                                                        } else {
                                                            itemCfg.dropDownCfg.selected = null;
                                                        }
                                                    }
                                                } else if ( itemCfg && itemCfg.type === this.rtfConst.custoDropDown ) {
                                                    itemCfg.dropDownCfg.selected = this._getAppliedCustomInlineStyleKey( currentStyle, item );
                                                } else if ( item.toLocaleLowerCase() === 'link' ) {
                                                    isActive = this._getLinkURL() !== undefined;
                                                } else {
                                                    isActive = currentStyle.has( item.toLocaleUpperCase() );
                                                }
                                                    
                                                if ( itemCfg ) {
                                                    return <RTFControlItem key={ item } active={ isActive } refreshDropdown={ this.refreshDropdown }
                                                        onToggle={ itemCfg.onClickFn ? itemCfg.onClickFn : itemCfg.styleType === 'block' ? this._toggleBlockType.bind(this) : this._toggleInlineStyle.bind(this) }
                                                        config={ itemCfg } store={ this.props.store } />
                                                } else {
                                                    return undefined;
                                                }
                                            } )
                                        }
                                    </div>
                                )
                            } else {
                                return undefined;
                            }
                         } )
                    })()
                }
                {
                    ( this.state.showLinkBox || this.state.showImageUploadBox ) && 
                        <div className='rtf_link_box_mask'></div>
                }
                {
                    this.state.showLinkBox && 
                        <div className='rtf_link_box'>
                            <div className='rtf_link_box_header'> Insert link <span id='rtf_link_box_close' className='icon-close float_right' onClick={ this._hideLinkBox.bind(this) }></span></div>
                            <div className='rtf_link_box_content'>
                                <label>URL</label>
                                <input type='text' id='rtf_link_url' defaultValue={ this.state.linkUrl }></input>
                            </div>
                            <div className='rtf_link_box_footer'>
                                <span onClick={ this._hideLinkBox.bind(this) }>Cancel</span>
                                <span className='bold' onMouseDown={ this._addLink.bind(this) }>Ok</span>
                            </div>
                        </div>
                }
                {
                    this.state.showImageUploadBox &&
                        <div className='image_upload_box'>
                            <div className='image_upload_header'> Insert image <span id='image_upload_box_close' className='icon-close float_right' onClick={ this._hideImageUploadBox.bind(this) }></span></div>
                            <div className='image_upload_content'>
                                <label className='image_type'>Upload</label>
                                <Select
                                    className='upload_type_select'
                                    defaultValue={ uploadOptions.filter( a_option => a_option.value === this.state.uploadImageType )[0] }
                                    isClearable={ false }
                                    isSearchable={ false }
                                    name="uploadtype"
                                    options={uploadOptions}
                                    onChange={ this._changeImageUploadType.bind(this) }
                                    />
                                <br></br>
                                <label className='image_source'>Source</label>
                                {
                                    ( this.state.uploadImageType === 'url' &&
                                        <input type='text' id='imageurl' onChange={ this._onImageUrlChange.bind(this) }></input> ) ||
                                        <input type='file'onChange={ this._onImageUpload.bind(this) }></input>
                                }
                            </div>
                            <div className='rtf_link_box_footer'>
                                <span onClick={ this._hideImageUploadBox.bind(this) }>Cancel</span>
                                <span className='bold' onMouseDown={ this._uploadImage.bind(this) }>Ok</span>
                            </div>
                            {
                                this.state.image && this.state.uploadImageType === 'internal' &&
                                    <img src={ this.state.image } id='temprtfimage' alt='showhide'></img>
                            }
                        </div>
                }
                {
                    this.state.shortcustImagePaste && this.state.uploadImageType === 'internal' &&
                        <img src={ this.state.image } id='temprtfimage' alt='showhide' style={{
                            position: 'fixed', top: '-100%', left: '-100%', height: '280px', width: '500px', visibility: 'hidden'}}
                            onLoad={ this._onCopyPasteImageLoad.bind(this) }></img>
                }
            </div>
        );
    };

    /* istanbul ignore next */
    _onCopyPasteImageLoad() {
        this._uploadImage();
        this.setState({
            shortcustImagePaste: false
        });
    };

    /* istanbul ignore next */
    _onImageUrlChange(event) {
        this.setState({
            image: event.target.value
        });
    };

    /* istanbul ignore next */
    _uploadImage() {
        if ( this.state.image ) {
            const that = this;

            if ( this.state.uploadImageType === 'internal' ) {
                const url = properties.url + APP_URL_CONSTANTS.SAVE_IMAGE_URL + "/" + AppHelper.getParamData('sologinid');
                const data = FileUtil.getBase64Image(document.getElementById('temprtfimage')).replace('data:image/png;base64,', '');

                AppUtil.ajax(url, {
                    image: data,
                    mimetype: 'image/png',
                    enterpriseid: AppHelper.getParamData('icentid')
                }, result => {

                    const imageURL = result.url.startsWith('https') ? result.url : result.url.replace(AppHelper.getParamData('issuecollectordataurl') + '/', window.location.origin + properties.url);

                    that._addImage(imageURL);
                    that._hideImageUploadBox();

                    that.setState({
                        image: undefined
                    });
               });
            } else if ( document.getElementById('imageurl').value === '' ) {
                this.props.store.dispatch( showMessage({
                    type: BOARD_MESSAGE_CONSTANT.TYPE_WARNING,
                    message: 'Select an image from your device.'
                }) );
    
                this.setState({
                    image: undefined
                });
            } else {
                this._addImage( document.getElementById('imageurl').value );
                this._hideImageUploadBox();
            }
        } else {
            this.props.store.dispatch( showMessage({
                type: BOARD_MESSAGE_CONSTANT.TYPE_WARNING,
                message: 'Provide image.'
            }) );   
        }
    };

    /* istanbul ignore next */
    onRTFPaste(e) {
        if ( e.clipboardData && e.clipboardData.files.length > 0 ) {
            this._processUploadedImage( e.clipboardData.files[0] );

            this.setState({
                uploadImageType: 'internal',
                shortcustImagePaste: true
            });
        }
    };

    /* istanbul ignore next */
    _addImage(url) {
        const that = this;
        const editorState = this.state.editorState;
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity( "image", "IMMUTABLE", { src: url });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.set( editorState, { currentContent: contentStateWithEntity }, "create-entity" );

        this.setState({
            editorState: AtomicBlockUtils.insertAtomicBlock( newEditorState, entityKey, " ")
        }, () => {
            that.props.onChange( that._getRTFContent() );
            setTimeout(() => this.focus(), 0);
        });
    };

    /* istanbul ignore next */
    _onImageUpload(e) {
        const file = e.target.files[0];

        this._processUploadedImage(file, e)
    };

    /* istanbul ignore next */
    _processUploadedImage(file, e) {
        const w_fileType = file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length).toLowerCase();

        if ( this.supportedImageTypes.indexOf(w_fileType) === -1 ) {
            this.props.store.dispatch( showMessage({
                type: BOARD_MESSAGE_CONSTANT.TYPE_WARNING,
                message: 'Select a valid image type.'
            }) );

            this.setState({
                image: undefined
            });

            if ( e ) {
                e.target.value = '';
            }
        } else if ( file.size / 1024 > 8192 ) {
            this.props.store.dispatch( showMessage({
                type: BOARD_MESSAGE_CONSTANT.TYPE_WARNING,
                message: 'Image size exceeds 8MB size limit.'
            }) );

            this.setState({
                image: undefined
            });

            if ( e ) {
                e.target.value = '';
            }
        } else {
            this.setState({
                image: URL.createObjectURL( file )
            });
        }
    };

    /* istanbul ignore next */
    _changeImageUploadType(event) {
        this.setState({
            uploadImageType: event.value
        });
    };

    _getAppliedCustomInlineStyleKey( currentStyle, key ) {
        let styleKey;

        currentStyle.forEach(value => {
            if ( value && key && value.toLocaleLowerCase().startsWith( key.toLocaleLowerCase() ) ) {
                styleKey = value;
            }
        });

        return styleKey;
    };

    _getFontFamily () {
        return {
            type: this.rtfConst.custoDropDown,
            //styleType: 'block',
            key: 'RTF_FONT_FAMILY',
            dropDownCfg: {
                id: 'rtf_font_family',
                RTF_TYPE: this.rtfConst.fontFamily,
                title: 'Font family',
                list: [{
                    id: 'fontFamily_Lato',
                    text: 'Lato'
                }, {
                    id: 'fontFamily_Arial',
                    text: 'Arial'
                }, {
                    id: 'fontFamily_Courier_New',
                    text: 'Courier New'
                }, {
                    id: 'fontFamily_Comic_Sans_MS',
                    text: 'Comic Sans MS'
                }, {
                    id: 'fontFamily_Georgia',
                    text: 'Georgia'
                }, {
                    id: 'fontFamily_Helvetica',
                    text: 'Helvetica'
                }, {
                    id: 'fontFamily_Times_New_Roman',
                    text: 'Times New Roman'
                }, {
                    id: 'fontFamily_Verdana',
                    text: 'Verdana'
                }, {
                    id: 'fontFamily_Sans_Serif',
                    text: 'Sans Serif'
                }],
                selected: undefined,
                listStyle: {
                    width: '150px',
                    height: '120px'
                }
            }
        }
    }

    _getFontSize () {
        return {
            type: this.rtfConst.custoDropDown,
            ///styleType: 'block',
            key: 'RTF_FONT_SIZE',
            dropDownCfg: {
                id: 'rtf_font_size',
                RTF_TYPE: this.rtfConst.fontSize,
                title: 'Font sizes',
                list: [{
                    id: 'fontSize8pt',
                    text: '8pt'
                }, {
                    id: 'fontSize10pt',
                    text: '10pt'
                }, {
                    id: 'fontSize12pt',
                    text: '12pt'
                }, {
                    id: 'fontSize14pt',
                    text: '14pt'
                }, {
                    id: 'fontSize18pt',
                    text: '18pt'
                }, {
                    id: 'fontSize24pt',
                    text: '24pt'
                }, {
                    id: 'fontSize36pt',
                    text: '36pt'
                }],
                selected: undefined,
                listStyle: {
                    width: '60px',
                    height: '120px'
                }
            }
        }
    }

    _getBackgroundColor() {
        return {
            type: this.rtfConst.custoDropDown,
            key: 'RTF_BACKGROUND_COLOR',
            dropDownCfg: {
                id: 'rtf_background_color',
                RTF_TYPE: this.rtfConst.backgroundColor,
                title: 'Background color',
                selectedElCls: 'icon-A-text',
                list: [{
                    id: 'backgroundcolor_none',
                    style: {
                        backgroundColor: '#FFFFFF'
                    },
                    title: 'clear',
                    cls: 'icon-close'
                }, {
                    id: 'backgroundcolor_1',
                    style: {
                        backgroundColor: '#DCFFCC'
                    }
                }, {
                    id: 'backgroundcolor_2',
                    style: {
                        backgroundColor: '#9FDFCD'
                    }
                }, {
                    id: 'backgroundcolor_3',
                    style: {
                        backgroundColor: '#CAADDE'
                    }
                }, {
                    id: 'backgroundcolor_4',
                    style: {
                        backgroundColor: '#F6ECBF'
                    }
                }, {
                    id: 'backgroundcolor_5',
                    style: {
                        backgroundColor: '#B2E45D'
                    }
                }, {
                    id: 'backgroundcolor_6',
                    style: {
                        backgroundColor: '#FFFFC5'
                    }
                }, {
                    id: 'backgroundcolor_7',
                    style: {
                        backgroundColor: '#FF8080'
                    }
                }, {
                    id: 'backgroundcolor_8',
                    style: {
                        backgroundColor: '#BAABDA'
                    }
                }, {
                    id: 'backgroundcolor_9',
                    style: {
                        backgroundColor: '#C2E8CE'
                    }
                }],
                selected: undefined,
                listStyle: {
                    width: '262px',
                    height: '35px'
                }
            }
        }
    }

    _getFontColor() {
        return {
            type: this.rtfConst.custoDropDown,
            key: 'RTF_FONT_COLOR',
            dropDownCfg: {
                id: 'rtf_font_color',
                RTF_TYPE: this.rtfConst.fontColor,
                title: 'Text color',
                selectedElCls: 'icon-A-text',
                dropdownPostion: 'top',
                list: [{
                    id: 'fontcolor_none',
                    style: {
                        backgroundColor: '#ffffff'
                    },
                    title: 'clear',
                    cls: 'icon-close'
                }, {
                    id: 'fontcolor_1',
                    style: {
                        backgroundColor: '#BE7575'
                    }
                }, {
                    id: 'fontcolor_2',
                    style: {
                        backgroundColor: '#C886E5'
                    }
                }, {
                    id: 'fontcolor_3',
                    style: {
                        backgroundColor: '#DEB881'
                    }
                }, {
                    id: 'fontcolor_4',
                    style: {
                        backgroundColor: '#F8A978'
                    }
                }, {
                    id: 'fontcolor_5',
                    style: {
                        backgroundColor: '#305F72'
                    }
                }, {
                    id: 'fontcolor_6',
                    style: {
                        backgroundColor: '#EF6C57'
                    }
                }, {
                    id: 'fontcolor_7',
                    style: {
                        backgroundColor: '#679186'
                    }
                }, {
                    id: 'fontcolor_8',
                    style: {
                        backgroundColor: '#BAD7DF'
                    }
                }, {
                    id: 'fontcolor_9',
                    style: {
                        backgroundColor: '#61C0BF'
                    }
                }],
                selected: undefined,
                listStyle: {
                    width: '262px',
                    height: '35px'
                }
            }
        }
    }

    _getToolbarItem( toolbarItem ) {
        switch( toolbarItem ) {
            case 'bold':
                return {
                    cls: 'icon-bold',
                    style: 'BOLD',
                    title: 'Bold'
                }
            case 'italic':
                return {
                    cls: 'icon-italic',
                    style: 'ITALIC',
                    title: 'Italic'
                }
            case 'underline':
                return {
                    cls: 'icon-underlined',
                    style: 'UNDERLINE',
                    title: 'Underline'
                }
            case 'strikethrough':
                return {
                    cls: 'icon-strikethrough',
                    style: 'STRIKETHROUGH',
                    title: 'Strikethrough'
                }
            case 'fontfamily':
                return this._getFontFamily();
            case 'fontsize':
                return this._getFontSize();
            case 'backgroundcolor':
                return this._getBackgroundColor();
            case 'fontcolor':
                return this._getFontColor();
            case 'justifyleft':
                return {
                    cls: 'icon-aligned-left',
                    title: 'Align left',
                    style: this.rtfConst.justifyLeft,
                    styleType: 'block'
                }
            case 'justifyright':
                return {
                    cls: 'icon-aligned-right',
                    title: 'Align right',
                    style: this.rtfConst.justifyRight,
                    styleType: 'block'
                }
            case 'justifycenter':
                return {
                    cls: 'icon-aligned-center',
                    title: 'Align center',
                    style: this.rtfConst.justifyCenter,
                    styleType: 'block'
                }
            case 'justify':
                return {
                    cls: 'icon-justified-paragraph',
                    title: 'Justify',
                    style: this.rtfConst.justify,
                    styleType: 'block'
                }
            case 'unordered-list-item':
                return {
                    cls: 'icon-bullet-list',
                    title: 'Bullet list',
                    style: 'unordered-list-item',
                    styleType: 'block'
                }
            case 'ordered-list-item':
                return {
                    cls: 'icon-number-list',
                    title: 'Numbered list',
                    style: 'ordered-list-item',
                    styleType: 'block'
                }
            case 'link':
                return {
                    onClickFn: this._showLinkBox.bind(this),
                    title: 'Insert/Edit link',
                    cls: 'icon-link'
                }
            case 'removelink':
                return {
                    onClickFn: this._removeLink.bind(this),
                    title: 'Remove link',
                    cls: 'icon-break'
                }
            case 'fullscreen':
                return {
                    onClickFn: this._toggleFullscreen.bind(this),
                    cls: this.state.fullscreen ? 'icon-restore' : 'icon-move',
                    title: !this.state.fullscreen ? 'Fullscreen' : 'Restore'
                }
            case 'image':
                return {
                    onClickFn: this._showImageUploadBox.bind(this),
                    title: 'Insert image',
                    cls: 'icon-landscape'
                }
            default:
                return undefined;
        }
    };

    _showImageUploadBox() {
        this.setState({
            showImageUploadBox: true
        });
    };

    _toggleFullscreen() {
        this.setState({
            fullscreen: !this.state.fullscreen
        });
    };

    /* istanbul ignore next */
    _showLinkBox() {
        const currentSelection = this.state.editorState.getSelection();

        if ( currentSelection.isCollapsed() ) {
            this.props.store.dispatch( showMessage({
                type: BOARD_MESSAGE_CONSTANT.TYPE_WARNING,
                message: 'select some content'
            }) );
        } else {

            this.setState({
                linkUrl: this._getLinkURL(),
                showLinkBox: true
            }, () => {
                document.getElementById('rtf_link_url').focus();                
            })
        }
    };

    _getLinkURL() {
        const contentState = this.state.editorState.getCurrentContent();
        const selection = this.state.editorState.getSelection();
        const startKey = selection.getStartKey();
        const startOffset = selection.getStartOffset();
        const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
        const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

        let url = null;

        if ( linkKey ) {
            const linkInstance = contentState.getEntity(linkKey);
            url = linkInstance.getData().url;
        }

        return url;
    };

    _hideLinkBox() {
        this.setState({
            showLinkBox: false
        })
    };

    _hideImageUploadBox() {
        this.setState({
            showImageUploadBox: false,
            image: undefined
        });
    };

    _getCustomStyleMap() {
        return {
            fontSize8pt: {
                fontSize: '8pt'
            },
            fontSize10pt: {
                fontSize: '10pt'
            },
            fontSize12pt: {
                fontSize: '12pt'
            },
            fontSize14pt: {
                fontSize: '14pt'
            },
            fontSize18pt: {
                fontSize: '18pt'
            },
            fontSize24pt: {
                fontSize: '24pt'
            },
            fontSize36pt: {
                fontSize: '36pt'
            },
            fontFamily_Lato: {
                fontFamily: 'Lato-Regular'
            },
            fontFamily_Arial: {
                fontFamily: 'arial'
            },
            fontFamily_Courier_New: {
                fontFamily: 'courier new'
            },
            fontFamily_Comic_Sans_MS: {
                fontFamily: 'comic sans ms'
            },
            fontFamily_Georgia: {
                fontFamily: 'georgia'
            },
            fontFamily_Helvetica: {
                fontFamily: 'helvetica'
            },
            fontFamily_Times_New_Roman: {
                fontFamily: 'times new roman'
            },
            fontFamily_Verdana: {
                fontFamily: 'verdana'
            },
            fontFamily_Sans_Serif: {
                fontFamily: 'sans-serif'
            },
            backgroundcolor_1: {
                backgroundColor: '#DCFFCC'
            },
            backgroundcolor_2: {
                backgroundColor: '#9FDFCD'
            },
            backgroundcolor_3: {
                backgroundColor: '#CAADDE'
            },
            backgroundcolor_4: {
                backgroundColor: '#F6ECBF'
            },
            backgroundcolor_5: {
                backgroundColor: '#B2E45D'
            },
            backgroundcolor_6: {
                backgroundColor: '#FFFFC5'
            },
            backgroundcolor_7: {
                backgroundColor: '#FF8080'
            },
            backgroundcolor_8: {
                backgroundColor: '#BAABDA'
            },
            backgroundcolor_9: {
                backgroundColor: '#C2E8CE'
            },
            fontcolor_1: {
                color: '#BE7575'
            },
            fontcolor_2: {
                color: '#C886E5'
            },
            fontcolor_3: {
                color: '#DEB881'
            },
            fontcolor_4: {
                color: '#F8A978'
            },
            fontcolor_5: {
                color: '#305F72'
            },
            fontcolor_6: {
                color: '#EF6C57'
            },
            fontcolor_7: {
                color: '#679186'
            },
            fontcolor_8: {
                color: '#BAD7DF'
            },
            fontcolor_9: {
                color: '#61C0BF'
            }
        };
    };

    /* istanbul ignore next */
    _toggleInlineStyle( style ) {
        const that = this;
        let prevStyleClearKey;

        if ( style.startsWith( this.rtfConst.fontFamily ) ) {
            prevStyleClearKey = this.rtfConst.fontFamily;
        } else if ( style.startsWith( this.rtfConst.fontSize ) ) {
            prevStyleClearKey = this.rtfConst.fontSize;
        } else if ( style.startsWith( this.rtfConst.bgColor ) ) {
            prevStyleClearKey = this.rtfConst.bgColor;
        } else if ( style.startsWith( this.rtfConst.fontColor ) ) {
            prevStyleClearKey = this.rtfConst.fontColor;
        }

        if ( prevStyleClearKey ) {
            const currentStyle = this.state.editorState.getCurrentInlineStyle();

            currentStyle.forEach(value => {
                if ( value.startsWith( prevStyleClearKey ) ) {
                    that.onRtfChange( RichUtils.toggleInlineStyle( that.state.editorState, value ) );        
                }
            });
        }

        if ( style === 'backgroundcolor_none' || style === 'fontcolor_none' ) {
            return false;
        }

        if ( this.props.onChange ) {
            this.props.onChange( this._getRTFContent() );
        }

        setTimeout(function () {
            that.onRtfChange( RichUtils.toggleInlineStyle( that.state.editorState, style ) );
        }, 0);
    };

    /* istanbul ignore next */
    _toggleBlockType( style, props ) {
        const that = this;

        if ( this.props.onChange ) {
            this.props.onChange( this._getRTFContent() );
        }

        setTimeout(function () {
            that.onRtfChange( RichUtils.toggleBlockType( that.state.editorState, 
                props && props.dropdowncfg && props.dropdowncfg.RTF_TYPE === that.rtfConst.fontFamily ? props.dropdowncfg.RTF_TYPE : style ) );
        }, 0);
    };

    /* istanbul ignore next */
    _getBlockStyle( block, value ) {
        switch ( value || block.getType() ) {
            case 'blockquote':
                return 'RichEditor-blockquote';
            case this.rtfConst.justifyLeft:
                return 'rtf_justify_left';
            case this.rtfConst.justifyRight:
                return 'rtf_justify_right';
            case this.rtfConst.justifyCenter:
                return 'rtf_justify_center';
            case this.rtfConst.justify:
                return 'rtf_justify';
            default: 
                return null;
        }
    };

    /* istanbul ignore next */
    _addLink() {
        const linkURL = document.getElementById('rtf_link_url').value.trim();
        const that = this;

        if ( linkURL !== '' ) {
            const contentState = this.state.editorState.getCurrentContent();
            const contentStateWithEntity = contentState.createEntity(
                'LINK',
                'MUTABLE',
                { url:  linkURL });
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
            const newEditorState = EditorState.set( this.state.editorState, { currentContent: contentStateWithEntity } );

            this.setState({ 
                editorState: RichUtils.toggleLink( newEditorState, newEditorState.getSelection(), entityKey ),
                showLinkBox: false
            }, () => {
                if ( that.props.onChange ) {
                    that.props.onChange( that._getRTFContent() );
                }
            });
        } else {
            this.props.store.dispatch( showMessage({
                    type: BOARD_MESSAGE_CONSTANT.TYPE_WARNING,
                    message: 'URL field is empty'
            }) );

            document.getElementById('rtf_link_url').focus();
        }
    };

    /* istanbul ignore next */
    _removeLink() {
        const selection = this.state.editorState.getSelection();

        if ( !selection.isCollapsed() ) {
            this.setState({
                editorState: RichUtils.toggleLink( this.state.editorState, selection, null )
            });
        }
    };

    /* istanbul ignore next */
    _Link( props ) {
        const { url } = props.contentState.getEntity( props.entityKey ).getData();

        return (
          React.createElement( 'a', { href: url,
                style: {
                    color: '#3b5998',
                    textDecoration: 'underline'
                } 
            }, props.children )
        );
    };
    /* istanbul ignore next */
    _Font( props ) {
        const { color, bgcolor, size, fontWeight, fontStyle } = props.contentState.getEntity( props.entityKey ).getData();

        return (
          React.createElement( 'font', { 
                size,
                color,
                style: {
                    backgroundColor: bgcolor,
                    fontWeight,
                    fontStyle
                }
            }, props.children )
        );
    };

    /* istanbul ignore next */
    _getRTFContent() {
        const that = this;

        return convertToHTML( {
            styleToHTML: style => {
                const styleMap = that._getCustomStyleMap( style );

                if ( style === 'STRIKETHROUGH' ) {
                    return <span style={ { textDecoration: 'underline line-through' } } />
                } else if ( styleMap[style] ) {
                    return <div data_style={ style } style={ styleMap[style] } />;
                } else {
                    return undefined;
                }
            },
            entityToHTML: ( entity, originalText ) => {
                if ( entity.type === 'LINK' ) {
                    return <a href={ entity.data.url }>{ originalText }</a>;
                } else if ( entity.type === 'image' ) {
                    return <img src={ entity.data.src } alt='draftRTF'/>;
                }

                return originalText;
            },
            blockToHTML: block => {
                if ( block.type === this.rtfConst.justifyLeft ) {
                    return <div style={ { textAlign: 'left' } }/>
                } else if ( block.type === this.rtfConst.justifyCenter ) {
                    return <div style={ { textAlign: 'center' } }/>
                }  else if ( block.type === this.rtfConst.justifyRight ) {
                    return <div style={ { textAlign: 'right' } }/>
                }  else if ( block.type === this.rtfConst.justify ) {
                    return <div style={ { textAlign: 'justify' } }/>
                }
            }
        } )( this.state.editorState.getCurrentContent() );
    };

    /* istanbul ignore next */
    _getRTFStateFromHTML() {
        return convertFromHTML( {
            htmlToStyle: ( nodeName, node, currentStyle ) => {
                const bgStyleNode = node.querySelector('div[style*="background-color"]');
                const colorStyleNode = node.querySelector('div[style*="color"]');
                const strikeThroghNode = node.querySelector('span[style*="text-decoration:underline line-through"]');
                const fontFamilyNode = node.querySelector('div[style*="font-family"]');
                const fontSizeNode = node.querySelector('div[style*="font-size"]');

                if ( fontSizeNode && !currentStyle.contains( fontSizeNode.getAttribute('data_style') ) ) {
                    return currentStyle.add( fontSizeNode.getAttribute('data_style') );
                }
                
                if ( fontFamilyNode && !currentStyle.contains( fontFamilyNode.getAttribute('data_style') ) ) {
                    return currentStyle.add( fontFamilyNode.getAttribute('data_style') );
                }

                if ( strikeThroghNode && !currentStyle.contains( 'STRIKETHROUGH' ) ) {
                    return currentStyle.add( 'STRIKETHROUGH' );
                }
                
                if ( colorStyleNode && !currentStyle.contains( colorStyleNode.getAttribute('data_style') ) ) {
                    return currentStyle.add( colorStyleNode.getAttribute('data_style') );
                }
                
                if ( bgStyleNode && !currentStyle.contains( bgStyleNode.getAttribute('data_style') ) ) {
                    return currentStyle.add( bgStyleNode.getAttribute('data_style') );
                }

                return currentStyle;
            },
            
            htmlToEntity: ( nodeName, node, createEntity ) => {
                if ( nodeName === 'a' ) {
                    return createEntity(
                        'LINK',
                        'MUTABLE',
                        { url: node.href }
                    );
                } else if ( nodeName === 'img' ) {
                    return createEntity(
                        'image',
                        'IMMUTABLE',
                        { src: node.src }
                    );
                } else if ( nodeName === 'font' ) {
                    return createEntity(
                        'FONT',
                        'IMMUTABLE',
                        { 
                            color: node.color, 
                            bgcolor:node.style["background-color"],
                            size: node.size
                        }
                    );
                }
            },

            htmlToBlock: ( nodeName, node ) => {
                if ( node.querySelector('div[style="text-align:left"]') ) {
                    return {
                        type: 'justifyLeft'
                    }
                } else if ( node.querySelector('div[style="text-align:right"]') ) {
                    return {
                        type: 'justifyRight'
                    }
                } else if ( node.querySelector('div[style="text-align:center"]') ) {
                    return {
                        type: 'justifyCenter'
                    }
                } else if ( node.querySelector('div[style="text-align:justify"]') ) {
                    return {
                        type: 'justify'
                    }
                } else if (nodeName === 'img') {
                    return {
                      type: 'atomic',
                      data: { src: node.src, caption: node.alt }
                    };
                }
            }
        } )( this.props.config.defaultValue );
    };
}

export default RTF;